import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate, useRouteContext } from '@tanstack/react-router';
import {
  Avatar,
  Button,
  ConfigProvider,
  Empty,
  Flex,
  Modal,
  Progress,
  Spin,
  Timeline,
  Typography,
  message,
} from 'antd';
import dayjs from 'dayjs';
import { ChevronLeft, CircleStop } from 'lucide-react';
import { useState } from 'react';

import {
  getLoadAiAgentRecordDetailsUrl,
  loadAiAgentRecordDetails,
  useGetAiAgentThread,
} from '../../../../api';
import { AgentRecordEntity } from '../../../../modules/ai-agent/components/agent-activity';
import { AgentLayout } from '../../../../modules/ai-agent/components/agent-layout';
import {
  AgentRecordRun,
  AgentRecordSteps,
} from '../../../../modules/ai-agent/components/agent-record';
import { ExecutionDetailDrawer } from '../../../../modules/ai-agent/components/execution-detail-drawer';
import {
  AGENT_RECORD_RUNS_PAGE_SIZE,
  AGENT_STEP_STATUSES,
  AGENT_STEP_STATUS_BY_RUN,
  THREAD_STATUS_LABELS,
  THREAD_STATUS_TONES,
} from '../../../../modules/ai-agent/constants';
import {
  useCancelAiAgentThreadMutation,
  useGetAiAgentQuery,
} from '../../../../modules/ai-agent/hooks';
import { getRecordProgress } from '../../../../modules/ai-agent/utils/getRecordProgress';
import { getStepIds } from '../../../../modules/ai-agent/utils/getStepIds';
import { Loader } from '../../../../ui/components/loader';
import { StatusTag } from '../../../../ui/components/status-tag';
import { TextTertiary } from '../../../../ui/components/text';
import { useInfiniteScroll } from '../../../../ui/hooks';

import type { AgentRecordRunProps } from '../../../../modules/ai-agent/components/agent-record';

const AgentRecordRoute = () => {
  const navigate = useNavigate();
  const { selectedOrganization } = useRouteContext({ from: '/_authenticated/_app' });
  const { agentId, entityId } = Route.useParams();
  const organizationId = selectedOrganization.id;

  const [visibleRuns, setVisibleRuns] = useState(AGENT_RECORD_RUNS_PAGE_SIZE);
  const [hover, setHover] = useState<AgentRecordRunProps['hover']>(null);
  const [openedThreadId, setOpenedThreadId] = useState<string | null>(null);

  const steps = useGetAiAgentQuery(organizationId, agentId).data?.data.steps ?? [];
  const cancelMutation = useCancelAiAgentThreadMutation(organizationId, agentId);
  const { data, isPending } = useQuery({
    queryKey: [getLoadAiAgentRecordDetailsUrl(organizationId, agentId), [entityId]] as const,
    queryFn: () => loadAiAgentRecordDetails(organizationId, agentId, { entityIds: [entityId] }),
    refetchInterval: (query) =>
      query.state.data?.data.threads.some(({ status }) => status === 'running') ? 5_000 : false,
  });

  const threads = data?.data.threads ?? [];
  const { data: progressRun } = useGetAiAgentThread(
    organizationId,
    agentId,
    threads.length > 0 ? getRecordProgress(threads, steps).run.id : '',
    { query: { enabled: threads.length > 0 } },
  );

  const sentinelRef = useInfiniteScroll({
    onReachEnd: () => setVisibleRuns((count) => count + AGENT_RECORD_RUNS_PAGE_SIZE),
  });

  const backToActivity = () => navigate({ to: '/agents/$agentId/activity', params: { agentId } });

  if (isPending) {
    return <Loader size="large" />;
  }

  if (!data || threads.length === 0) {
    return (
      <Flex flex={1} vertical align="center" justify="center" gap={16}>
        <Empty description="No runs on this record" />
        <Button onClick={backToActivity}>Back to Activity</Button>
      </Flex>
    );
  }

  const lastRun = threads[0];
  const recordStatus = lastRun.status === 'rescheduled' ? 'scheduled' : lastRun.status;
  const { run: currentRun } = getRecordProgress(threads, steps);
  const { statuses } = getRecordProgress(
    threads,
    steps,
    ['running', 'blocked', 'failed', 'canceled'].includes(currentRun.status)
      ? getStepIds(
          (progressRun?.data.nodes ?? []).flatMap((node) =>
            node.type === 'message' ? (node.payload.toolCalls ?? []) : [],
          ),
        ).at(-1)
      : undefined,
  );
  const runs = [...threads].reverse().slice(0, visibleRuns);

  const backButton = (
    <Button
      icon={<ChevronLeft size={14} />}
      style={{ fontSize: 12, padding: '8px 10px' }}
      onClick={backToActivity}
    >
      Activity
    </Button>
  );

  return (
    <>
      <AgentLayout
        mobileHeaderLeft={backButton}
        leftHeader={
          <Flex align="center" gap={8} flex={1}>
            {backButton}
            <AgentRecordEntity entityId={entityId} detail={data.data} />
            <StatusTag {...THREAD_STATUS_TONES[recordStatus]}>
              {THREAD_STATUS_LABELS[recordStatus]}
            </StatusTag>
          </Flex>
        }
        left={
          <Flex vertical flex={1} style={{ overflowY: 'auto', padding: 20 }}>
            <ConfigProvider theme={{ components: { Timeline: { dotSize: 24, tailColor: 'var(--ant-color-border)' } } }}>
              <Timeline
                styles={{ itemIcon: { width: 24, height: 24, marginInlineStart: -7 } }}
                items={runs.map((run) => {
                  const { icon: Icon, color } =
                    AGENT_STEP_STATUSES[
                      AGENT_STEP_STATUS_BY_RUN[run.status === 'rescheduled' ? 'completed' : run.status]
                    ];
                  return {
                    key: run.id,
                    icon: (
                      <Avatar
                        size={24}
                        icon={<Icon size={12} />}
                        style={{ backgroundColor: color }}
                      />
                    ),
                    content: (
                      <AgentRecordRun
                        organizationId={organizationId}
                        agentId={agentId}
                        run={run}
                        steps={steps}
                        hover={hover}
                        onHover={setHover}
                        onOpenDetails={() => setOpenedThreadId(run.id)}
                      />
                    ),
                  };
                })}
              />
            </ConfigProvider>
            {visibleRuns < threads.length && (
              <Flex ref={sentinelRef} justify="center" style={{ padding: 16 }}>
                <Spin size="small" />
              </Flex>
            )}
          </Flex>
        }
        rightHeader={
          <Flex align="center" justify="space-between" gap={8} flex={1}>
            <Flex align="center" gap={12}>
              <Typography.Text strong>Steps</Typography.Text>
              <Progress
                steps={steps.length}
                percent={100}
                showInfo={false}
                size={[14, 6]}
                strokeColor={statuses.map((status) => AGENT_STEP_STATUSES[status].color)}
              />
              <TextTertiary style={{ fontSize: 12 }}>
                {statuses.filter((status) => status === 'done').length} of {steps.length} done
              </TextTertiary>
            </Flex>
            <TextTertiary style={{ fontSize: 12 }}>
              {threads.length === 1 ? '1 run' : `${threads.length} runs`} · since{' '}
              {dayjs(threads[threads.length - 1].createdAt).format('MMM D')}
            </TextTertiary>
            {(lastRun.status === 'scheduled' || lastRun.status === 'running') && (
              <Button
                variant="filled"
                color="red"
                size="small"
                icon={<CircleStop size="1em" />}
                onClick={() =>
                  Modal.confirm({
                    title: 'Stop this agent for this record?',
                    okText: 'Stop',
                    okType: 'danger',
                    onOk: () =>
                      cancelMutation
                        .mutateAsync(lastRun.id)
                        .then(() => message.success('Agent stopped for this record'))
                        .catch(() => message.error('Failed to stop the agent')),
                  })
                }
              >
                Stop
              </Button>
            )}
          </Flex>
        }
        right={
          <AgentRecordSteps
            steps={steps}
            statuses={statuses}
            detail={data.data}
            hover={hover}
            onHover={setHover}
          />
        }
      />
      <ExecutionDetailDrawer
        open={!!openedThreadId}
        organizationId={organizationId}
        agentId={agentId}
        aiAgentThreadId={openedThreadId}
        onClose={() => setOpenedThreadId(null)}
      />
    </>
  );
};

export const Route = createFileRoute('/_authenticated/_app/agents/$agentId_/activity/$entityId')({
  component: AgentRecordRoute,
});
